import type {
  ComplianceResult,
  HITLRequest,
  RiskResult,
  TradeSignal,
} from "@/types/agents";

type UnknownRecord = Record<string, unknown>;

export type NormalizedPendingHITLRequest = HITLRequest & {
  run_id?: string;
};

export function isActionablePendingHITLRequest(
  request: Pick<NormalizedPendingHITLRequest, "time_remaining_seconds">,
): boolean {
  return request.time_remaining_seconds > 0;
}

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeSignal(value: unknown): TradeSignal | null {
  const signal = asRecord(value);
  if (!signal) return null;
  const direction = signal.direction;
  const confidence = finiteNumber(signal.confidence);
  const symbol = nonEmptyString(signal.symbol);
  const entryPrice = finiteNumber(signal.entry_price);
  const positionSize = finiteNumber(signal.position_size);
  const reasoning = asRecord(signal.reasoning);
  if (
    (direction !== "long" && direction !== "short" && direction !== "flat") ||
    confidence === null ||
    confidence < 0 ||
    confidence > 1 ||
    !symbol ||
    entryPrice === null ||
    positionSize === null ||
    positionSize <= 0 ||
    !reasoning
  ) {
    return null;
  }
  const stopLoss = finiteNumber(signal.stop_loss);
  const takeProfit = finiteNumber(signal.take_profit);
  if (
    (signal.stop_loss !== undefined && stopLoss === null) ||
    (signal.take_profit !== undefined && takeProfit === null)
  ) {
    return null;
  }
  return {
    direction,
    confidence,
    symbol,
    entry_price: entryPrice,
    position_size: positionSize,
    reasoning,
    ...(stopLoss !== null ? { stop_loss: stopLoss } : {}),
    ...(takeProfit !== null ? { take_profit: takeProfit } : {}),
  };
}

function normalizeRisk(value: unknown): RiskResult | null {
  const risk = asRecord(value);
  if (!risk || typeof risk.approved !== "boolean") return null;
  const riskScore = finiteNumber(risk.risk_score);
  const checks = asRecord(risk.checks);
  if (
    riskScore === null ||
    !checks ||
    Object.values(checks).some((check) => typeof check !== "boolean")
  ) {
    return null;
  }
  const adjustedSize = finiteNumber(risk.adjusted_size);
  const rejectionReason =
    risk.rejection_reason === undefined
      ? null
      : nonEmptyString(risk.rejection_reason);
  if (
    (risk.adjusted_size !== undefined && adjustedSize === null) ||
    (risk.rejection_reason !== undefined && rejectionReason === null)
  ) {
    return null;
  }
  return {
    approved: risk.approved,
    risk_score: riskScore,
    checks: checks as Record<string, boolean>,
    ...(adjustedSize !== null ? { adjusted_size: adjustedSize } : {}),
    ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
  };
}

function normalizeCompliance(value: unknown): ComplianceResult | null {
  const compliance = asRecord(value);
  if (!compliance || typeof compliance.approved !== "boolean") return null;
  const rulebookVersion = nonEmptyString(compliance.rulebook_version);
  const violatedRules = compliance.violated_rules;
  const ruleEvaluations = compliance.rule_evaluations;
  if (
    !rulebookVersion ||
    !Array.isArray(violatedRules) ||
    violatedRules.some((rule) => typeof rule !== "string") ||
    !Array.isArray(ruleEvaluations)
  ) {
    return null;
  }
  const normalizedEvaluations = ruleEvaluations.map((evaluation) => {
    const record = asRecord(evaluation);
    const ruleId = nonEmptyString(record?.rule_id);
    const ruleName = nonEmptyString(record?.rule_name);
    const reason = nonEmptyString(record?.reason);
    if (
      !record ||
      !ruleId ||
      !ruleName ||
      !reason ||
      typeof record.passed !== "boolean"
    ) {
      return null;
    }
    return {
      rule_id: ruleId,
      rule_name: ruleName,
      passed: record.passed,
      reason,
    };
  });
  if (normalizedEvaluations.some((evaluation) => evaluation === null)) {
    return null;
  }
  const complianceScore = finiteNumber(compliance.compliance_score);
  if (compliance.compliance_score !== undefined && complianceScore === null) {
    return null;
  }
  return {
    approved: compliance.approved,
    violated_rules: violatedRules,
    rulebook_version: rulebookVersion,
    rule_evaluations:
      normalizedEvaluations as ComplianceResult["rule_evaluations"],
    ...(complianceScore !== null ? { compliance_score: complianceScore } : {}),
  };
}

export function normalizePendingHITLRequest(
  value: unknown,
): NormalizedPendingHITLRequest | null {
  const row = asRecord(value);
  if (!row) return null;
  const id = nonEmptyString(row.id);
  const expiresAt = nonEmptyString(row.expires_at);
  const createdAt = nonEmptyString(row.created_at);
  const timeRemaining = finiteNumber(row.time_remaining_seconds);
  if (
    !id ||
    !expiresAt ||
    Number.isNaN(new Date(expiresAt).getTime()) ||
    !createdAt ||
    Number.isNaN(new Date(createdAt).getTime()) ||
    timeRemaining === null ||
    timeRemaining < 0 ||
    (row.status !== undefined && row.status !== "pending")
  ) {
    return null;
  }

  const rawSnapshot = asRecord(row.signal_snapshot);
  const decisionContext =
    asRecord(rawSnapshot?.decision_context) ??
    (rawSnapshot &&
    ("signal" in rawSnapshot ||
      "risk" in rawSnapshot ||
      "compliance" in rawSnapshot)
      ? rawSnapshot
      : null);
  const signal = normalizeSignal(decisionContext?.signal ?? rawSnapshot);
  const risk = normalizeRisk(decisionContext?.risk ?? row.risk_summary);
  const compliance = normalizeCompliance(
    decisionContext?.compliance ?? row.compliance_summary,
  );
  if (!signal || !risk || !compliance) return null;

  const runId = nonEmptyString(row.run_id);
  const instanceId = nonEmptyString(row.instance_id);
  const correlationId = nonEmptyString(row.correlation_id);
  return {
    id,
    signal_snapshot: signal,
    risk_summary: risk,
    compliance_summary: compliance,
    status: "pending",
    expires_at: expiresAt,
    time_remaining_seconds: timeRemaining,
    created_at: createdAt,
    ...(runId ? { run_id: runId } : {}),
    ...(instanceId ? { instance_id: instanceId } : {}),
    ...(correlationId ? { correlation_id: correlationId } : {}),
  };
}
